import { shiftDayKey, warsawDayStart } from './timeline';
import type { DriverWeek, DriverWeekDriver } from './vehiclePlanApi';

/**
 * Kierowca tygodnia z „Harmonogramu planowania kierowców” — teksty i granice
 * odcinków na osi. Odpowiada `apps/api/src/vehicle-schedules/driver-weeks.ts`.
 *
 * Wpis z pliku podpisujemy „wg harmonogramu”, wiersz powielony (tymczasowe
 * powielenie ostatniego zaplanowanego miesiąca do czasu regularnego źródła)
 * — „przepisany z <miesiąc>”, żeby planista wiedział, że to nie plan z pliku.
 * Do 01.10.2026 ekran mówił „powielony”, co brzmiało jak błąd danych.
 */

const MONTHS_GENITIVE = [
  'stycznia',
  'lutego',
  'marca',
  'kwietnia',
  'maja',
  'czerwca',
  'lipca',
  'sierpnia',
  'września',
  'października',
  'listopada',
  'grudnia',
];

const pad = (n: number) => String(n).padStart(2, '0');

/** „wg harmonogramu” albo „przepisany z października”. */
export function driverWeekSource(d: DriverWeekDriver): string {
  if (!d.replicated) return 'wg harmonogramu';
  const month = d.replicatedFromWeek ? Number(d.replicatedFromWeek.slice(5, 7)) : NaN;
  const name = MONTHS_GENITIVE[month - 1];
  if (!name) return 'przepisany z wcześniejszego miesiąca';
  // „ze stycznia” — przed zbitką spółgłosek „z” przechodzi w „ze”.
  return `przepisany ${name.startsWith('st') ? 'ze' : 'z'} ${name}`;
}

/** Tekst odcinka: nazwiska, a przy statusie — status w nawiasie. */
export function driverWeekText(week: DriverWeek): string {
  return week.drivers.map((d) => (d.status ? `${d.name} (${d.status})` : d.name)).join(', ');
}

/** Czy cały tydzień jest powielony (pokazujemy go wtedy szaro, kursywą). */
export function isReplicatedWeek(week: DriverWeek): boolean {
  return week.drivers.length > 0 && week.drivers.every((d) => d.replicated);
}

/** „DD.MM” z dnia `RRRR-MM-DD`. */
const dayMonth = (key: string) => {
  const [, m, d] = key.split('-').map(Number);
  return `${pad(d)}.${pad(m)}`;
};

/**
 * „tydzień 40 (28.09–04.10)” — numer bez roku i bez „W” (API podaje
 * `2026-W40`, poprawa brzmienia 01.10.2026), z dniami od poniedziałku do
 * niedzieli. Numer nieczytelny — sam zakres dni.
 */
export function driverWeekLabel(week: Pick<DriverWeek, 'weekStart' | 'isoWeek'>): string {
  const match = /W(\d{1,2})$/.exec(week.isoWeek ?? '');
  const days = `${dayMonth(week.weekStart)}–${dayMonth(shiftDayKey(week.weekStart, 6))}`;
  return match ? `tydzień ${Number(match[1])} (${days})` : `tydzień ${days}`;
}

/** Dymek odcinka: tydzień, pod nim każdy kierowca ze statusem i źródłem. */
export function driverWeekTitle(week: DriverWeek): string {
  const label = driverWeekLabel(week);
  const lines = [`${label.charAt(0).toUpperCase()}${label.slice(1)}`];
  for (const driver of week.drivers) {
    const status = driver.status ? ` (${driver.status})` : '';
    lines.push(`${driver.name}${status} — ${driverWeekSource(driver)}`);
  }
  return lines.join('\n');
}

/** Granice tygodnia na osi: od poniedziałku 00:00 do następnego poniedziałku 00:00 w Polsce. */
export function driverWeekBounds(week: DriverWeek): { from: Date; to: Date } {
  return {
    from: warsawDayStart(week.weekStart),
    to: warsawDayStart(shiftDayKey(week.weekStart, 7)),
  };
}

/** Tydzień, w którym wypada dzień `RRRR-MM-DD` (dzień polski); `null`, gdy go nie ma. */
export function driverWeekOn(weeks: ReadonlyArray<DriverWeek> | undefined, dayKey: string): DriverWeek | null {
  return (
    (weeks ?? []).find((w) => w.weekStart <= dayKey && dayKey < shiftDayKey(w.weekStart, 7)) ?? null
  );
}
